import {
  FunctionComponent,
  MutableRefObject,
  ReactNode,
  createContext,
  useCallback,
  useContext,
} from 'react';
import { Point, Size } from '@/types';
import { hasOwnProperty } from '@/utils/hasOwnProperty';

interface TransformChangedCallbackPayloadData {
  cid: number;
  offset: Point;
  rotation: number;
  size: Size;
}

interface TransformChangedCallbackPayloadLast {
  last: true;
}

export type TransformChangedCallbackPayload =
  | TransformChangedCallbackPayloadData
  | TransformChangedCallbackPayloadLast;

export const isLastPayLoad = (
  payload: TransformChangedCallbackPayload
): payload is TransformChangedCallbackPayloadLast => hasOwnProperty(payload, 'last');

export type TransformChangedCallback = (transform: TransformChangedCallbackPayload) => void;

const TransformChangedContext = createContext<TransformChangedCallback | undefined>(undefined);

interface TransformChangedProviderProps {
  children: ReactNode;
  callbacks: MutableRefObject<Set<TransformChangedCallback>>;
}

export const TransformChangedProvider: FunctionComponent<TransformChangedProviderProps> = ({
  callbacks,
  children,
}) => {
  const transformChangedCallback = useCallback(
    (transform: TransformChangedCallbackPayload) => {
      callbacks.current.forEach((callback) => callback(transform));
    },
    [callbacks]
  );
  return (
    <TransformChangedContext.Provider value={transformChangedCallback}>
      {children}
    </TransformChangedContext.Provider>
  );
};

export const useTransformChangedCallback = () => useContext(TransformChangedContext);
